// Plans for Site Walks (Phelipe 2026-09-25, Site Walks list #9, #16, #18):
// the maths, no DOM. A plan is a picture of a drawing sheet; positions on it
// are fractions of its width and height (x right, y down). Tested in
// test/javascript/plan_geometry_test.mjs.

const M_PER_DEG_LAT = 111132
const M_PER_DEG_LON = 111320

// Where a plan's corners are on the ground - [[lat, lon] top-left, top-right,
// bottom-right, bottom-left] - from matching points [{ x, y, lat, lon }]: a
// spot on the plan and the same spot on the map. Two points fix its place,
// turn and scale; more are averaged. The plan keeps its shape (it's a drawing
// to scale). The same sums as SitePlan.corners_from on the server, so the
// preview is what gets saved. null for fewer than two distinct points.
export function cornersFromPoints(points, width, height) {
  const pairs = (points || []).filter((p) => [p?.x, p?.y, p?.lat, p?.lon].every(Number.isFinite))
  if (pairs.length < 2 || !(width > 0) || !(height > 0)) return null
  const lat0 = pairs.reduce((sum, p) => sum + p.lat, 0) / pairs.length
  const lon0 = pairs.reduce((sum, p) => sum + p.lon, 0) / pairs.length
  const k = Math.cos((lat0 * Math.PI) / 180)
  // Complex numbers as [re, im]: the plan with up positive, the ground in metres east and north.
  const plan = pairs.map((p) => [p.x * width, -p.y * height])
  const ground = pairs.map((p) => [(p.lon - lon0) * M_PER_DEG_LON * k, (p.lat - lat0) * M_PER_DEG_LAT])
  const mean = (list) => [list.reduce((s, c) => s + c[0], 0) / list.length, list.reduce((s, c) => s + c[1], 0) / list.length]
  const planMid = mean(plan)
  const groundMid = mean(ground)
  let spread = 0
  let turnRe = 0
  let turnIm = 0
  for (let i = 0; i < plan.length; i++) {
    const pr = plan[i][0] - planMid[0]
    const pi = plan[i][1] - planMid[1]
    const gr = ground[i][0] - groundMid[0]
    const gi = ground[i][1] - groundMid[1]
    spread += pr * pr + pi * pi
    // (g) × conj(p)
    turnRe += gr * pr + gi * pi
    turnIm += gi * pr - gr * pi
  }
  if (spread < 1e-6) return null
  turnRe /= spread
  turnIm /= spread
  const shiftRe = groundMid[0] - (turnRe * planMid[0] - turnIm * planMid[1])
  const shiftIm = groundMid[1] - (turnRe * planMid[1] + turnIm * planMid[0])
  const onGround = (x, y) => {
    const re = turnRe * x - turnIm * -y + shiftRe
    const im = turnRe * -y + turnIm * x + shiftIm
    return [lat0 + im / M_PER_DEG_LAT, lon0 + re / (M_PER_DEG_LON * k)]
  }
  return [onGround(0, 0), onGround(width, 0), onGround(width, height), onGround(0, height)]
}

// The CSS matrix that lays a width × height picture on its three corners
// (top-left, top-right, bottom-left), in screen pixels: matrix(a, b, c, d, e, f).
export function cornerMatrix(topLeft, topRight, bottomLeft, width, height) {
  return [
    (topRight.x - topLeft.x) / width, (topRight.y - topLeft.y) / width,
    (bottomLeft.x - topLeft.x) / height, (bottomLeft.y - topLeft.y) / height,
    topLeft.x, topLeft.y
  ]
}

// An indoor walk on a plan (#9): [[video seconds, x, y], ...] marked in the
// viewer, straight lines between the marks. Where it was at `seconds`
// (before the first mark: at the first; after the last: at the last).
export function planPointAt(points, seconds) {
  const list = sortedMarks(points)
  if (!list.length) return null
  if (seconds <= list[0][0]) return [list[0][1], list[0][2]]
  for (let i = 1; i < list.length; i++) {
    const [t1, x1, y1] = list[i - 1]
    const [t2, x2, y2] = list[i]
    if (seconds <= t2) {
      const along = t2 > t1 ? (seconds - t1) / (t2 - t1) : 1
      return [x1 + along * (x2 - x1), y1 + along * (y2 - y1)]
    }
  }
  const last = list[list.length - 1]
  return [last[1], last[2]]
}

// Which way the walk went along the plan at `seconds`, in degrees clockwise
// from the top of the plan; null standing still. `aspect` = the plan's
// height / width, so a diagonal of the picture reads as one.
export function planHeadingAt(points, seconds, aspect = 1) {
  const list = sortedMarks(points)
  if (list.length < 2) return null
  let i = list.findIndex((p) => p[0] >= seconds)
  if (i <= 0) i = i === 0 ? 1 : list.length - 1
  const [, x1, y1] = list[i - 1]
  const [, x2, y2] = list[i]
  const dx = x2 - x1
  const dy = (y2 - y1) * aspect
  if (Math.hypot(dx, dy) < 1e-6) return null
  return ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360
}

// The moment of the walk nearest a spot on the plan: { seconds, reach } with
// reach in plan widths; null for fewer than two marks.
export function nearestOnPlan(points, x, y) {
  const list = sortedMarks(points)
  if (list.length < 2) return null
  let best = null
  for (let i = 1; i < list.length; i++) {
    const [t1, x1, y1] = list[i - 1]
    const [t2, x2, y2] = list[i]
    const dx = x2 - x1
    const dy = y2 - y1
    const length = dx * dx + dy * dy
    const along = length ? Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / length)) : 0
    const reach = Math.hypot(x1 + along * dx - x, y1 + along * dy - y)
    if (!best || reach < best.reach) best = { seconds: t1 + along * (t2 - t1), reach }
  }
  return best
}

function sortedMarks(points) {
  return (Array.isArray(points) ? points : [])
    .filter((p) => Array.isArray(p) && p.length >= 3 && p.every(Number.isFinite))
    .slice()
    .sort((a, b) => a[0] - b[0])
}

// Plan space for Leaflet's flat map (CRS.Simple): a plan 1000 units wide,
// y up. Fractions of the picture ↔ [lat, lng] in that space.
export const PLAN_UNITS = 1000
export function planToLatLng(x, y, aspect) {
  return [(1 - y) * PLAN_UNITS * aspect, x * PLAN_UNITS]
}
export function latLngToPlan(lat, lng, aspect) {
  return [lng / PLAN_UNITS, 1 - lat / (PLAN_UNITS * aspect)]
}
